<template>
  <v-row no-gutters class="spectralis-comparison-wrapper" :style="borderStyle">
    <v-col cols="auto" class="sidebar-controls" :style="borderStyle">
      <span class="spectralis-multiview-title" :class="colorClass">{{name}}</span>
      <div>
        <select-with-button
          @change="selectExam"
          :value="exam"
          :items="exams"
          :icon-button="true"
          item-text="octDateButtonText"
          class="toolbar-select spectralis-multiview-dropdown"/>
      </div>
      <v-row no-gutters>
        <v-col cols="auto">
          <v-btn
            text
            v-if="hasExam"
            class="modal-cta spectralisCompareButton"
            :class="[{active: hasColorPalette}, colorClass]"
            @click="toggleColor">Color</v-btn>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="auto">
          <v-btn
            text
            v-if="hasExam"
            class="modal-cta spectralisCompareButton"
            :class="[{active: isInverted}, colorClass]"
            @click="invertColor">Invert</v-btn>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="auto">
          <v-btn
            text
            v-if="hasSegmentation"
            class="modal-cta spectralisCompareButton"
            :class="[{active: showSegmentation}, colorClass]"
            @click="toggleSegmentation">Segmentation</v-btn>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="auto">
          <v-btn
            text
            v-if="hasSegmentation"
            class="modal-cta spectralisCompareButton"
            :class="[{active: isMeasuring}, colorClass]"
            @click="measure">Measure</v-btn>
        </v-col>
      </v-row>
      <v-row no-gutters v-if="showThicknessComparison">
        <v-col cols="auto">
          <v-btn
            text
            class="modal-cta spectralisCompareButton"
            id="thickness-comparison"
            :class="[{active: showThicknessMap}, colorClass]"
            @click="toggleShowThicknessMap">
            <span class="d-xs-none d-sm-none d-md-none d-lg-flex">Thickness Comparison</span>
            <span class="d-flex d-lg-none">Compare</span>
          </v-btn>
        </v-col>
        <reset-slider
          v-if="showThicknessMap"
          :color="colorHex"
          :value="thicknessMapOpacity"
          :min="0"
          :max="1"
          :step="0.05"
          :defaultValue="1.0"
          @input="updateOpacity"
          name="Color Intensity"/>
      </v-row>
    </v-col>
    <v-col class="main-view">
      <v-row no-gutters class="main-view-layout">
        <v-col class="main-view-thickness d-flex">
          <span v-if="isCompare && isBothMeasuring && isLocked"
            class="spectralis-multiview-difference-measure">DIFFERENCE: {{thicknessDifference}}</span>
          <thickness-map
            v-if="thicknessMap && isSpectralisVolume"
            :mapId="name + '-thickness-map-wrapper'"
            :paletteId="name + '-thickness-map-palette-wrapper'"
            :img="thicknessMapImg"
            :size="refFit"
            :palette="thicknessMapPalette"
            :paletteSize="thicknessPaletteSize"
            :isLoading="thicknessMapImgLoading || octDataLoading"
            :imgNotFound="thicknessMapImgNotFound"
            :colorSettings="thicknessMapColorSettings"
            :minThickness="minThickness"
            :maxThickness="maxThickness"
            :thicknessStep="thicknessStep"
            :showETDRS="false"
            :foveaOffset="foveaOffset"
            :isMultiview="true"/>
        </v-col>
        <v-col class="main-view-reference d-flex">
          <oct-reference-image
            :referenceId="name + '-reference-image-wrapper'"
            :paletteId="thicknessMap && this.baseline? name + '-reference-palette-wrapper': ''"
            :isLoading="refImgLoading || octDataLoading"
            :refImgNotFound="refImgNotFound"
            :refImg="refImg"
            :refFit="refFit"        
            :refSize="refSize"
            :refImgLocation="refImgLocation"
            :slices="slices"
            :sliceCurrent="sliceCurrent"
            :sliceCurrentIndex="sliceCurrentIndex"
            :sliceBounds="sliceBounds"
            :isPlaying="isPlaying"
            :playRate="playRate"
            :playbackTop="true"
            :showExtension="true"
            :isSpectralisCircle="isSpectralisCircle"        
            :isSpectralisRadial="isSpectralisRadial"
            :isSpectralisRadialCircle="isSpectralisRadialCircle"
            :drawAllSlices="showSliceLines"
            :showOverlay="showThicknessMap"
            :overlayOpacity="differenceMapOpacity"
            :overlayImg="overlayImg"
            :overlaySize="overlaySize"
            :palette="overlayPalette"
            :paletteSize="overlayPaletteSize"
            :paletteMin="overlayMin"
            :paletteMax="overlayMax"
            :paletteStep="overlayStep"
            :isMultiview="true"
            @previous="previousSlice"
            @next="nextSlice"
            @play="play"
            @pause="pause"
            @setPlayRate="setPlayRate"
            @selectSlice="selectSlice"/>
        </v-col>
        <v-col class="main-view-slice"
          @wheel.prevent="wheel">
          <oct-slice-view
            :slice-id="name + '-slice-image-wrapper'"
            :fitSize="sliceFit"
            :img="sliceImg"
            :imgSize="sliceImgSize"
            :imgNotFound="sliceImgNotFound"
            :imgLoading="sliceImgLoading"
            :dataLoading="octDataLoading"
            :sliceEyeMarker="sliceCurrentEyeMarker"
            :showSegmentation="showSegmentation"
            :sliceInfo="sliceInfo"
            :layerData="sliceSegmentationLayerData"
            :isMeasuring="isMeasuring"
            :measureData="measureData"
            :transform="transform"
            :realLocation="sliceLocation"
            :isInBounds="sliceInBounds"
            :octColorSettings="colorSettings"
            @measurementChanged="measurementChanged"
            @previous="previousSlice"
            @next="nextSlice"/>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import selectWithButton from "../../controls/SelectWithButton";
import octReferenceImage from "./OCTReferenceImage";
import octSliceView from "./OCTSliceView";
import responsiveCanvases from "../../mixins/ResponsiveCanvases";
import resetSlider from "../../controls/ResetSlider";
import thicknessMap from "./ThicknessMap";
import { mapGetters } from "vuex";
import { mapWatchers } from "../../../helpers/utils";

import {
  defaultSize,
  defaultTransform,
  getViewSize,
  setSize,
  setFittedSize,
  setFitZoom,
  viewOffset,
  withinViewBounds,
  calculateRealLocation,
  setSpectralisReferenceFit,
  setTransformSizes
} from "../../../canvas/geometry";
export default {
  name: "spectralisComparisonView",
  components: { selectWithButton, octReferenceImage, octSliceView, resetSlider, thicknessMap },
  mixins: [responsiveCanvases],
  props: {
    name: String,
    context: Object,
    exams: Array,
    groups: Array
  },
  data() {
    return {
      referenceCursor: "",
      sliceCursor: "",
      refSize: defaultSize(),
      refFit: defaultSize(),
      sliceSize: defaultSize(),
      sliceFit: defaultSize(),
      sliceImgSize: defaultSize(),
      transform: defaultTransform(),
      thicknessMapSize: defaultSize(),
      thicknessPaletteSize: defaultSize(),
      overlaySize: defaultSize(),
      overlayPaletteSize: defaultSize(),
      refImgTransform: defaultTransform()
    };
  },
  methods: {
    updateCanvasSizes() {
      if (this.exams.length > 0) {
        let refId = this.name + "-reference-image-wrapper";
        let rvs = getViewSize(refId);
        setSize(this.refSize, rvs);
        setSpectralisReferenceFit(this.refSize, this.sliceBounds, this.refFit);

        let sliceId = this.name + "-slice-image-wrapper";
        let svs = getViewSize(sliceId);
        setSize(this.sliceSize, svs);
        let infoSize = this.sliceInfo && this.sliceInfo.dimensions;
        setSize(this.sliceImgSize, infoSize || svs);
        let sFit = setFittedSize(
          this.sliceSize,
          this.sliceImgSize,
          this.sliceFit
        );

        let mapId = this.name + "-thickness-map-wrapper";
        let tms = getViewSize(mapId);
        // ensure that thickness map has the same aspect ratio as the reference image map
        let size = {
          w: this.refFit.w * this.relativeMapSize.w,
          h: this.refFit.h * this.relativeMapSize.h
        };
        setFittedSize(tms, size, this.thicknessMapSize, 5);
        setSize(this.overlaySize, size);
        let paletteId = this.name + "-thickness-map-palette-wrapper";
        let tmps = getViewSize(paletteId);
        setFittedSize(tmps, tmps, this.thicknessPaletteSize, 5);

        let overlayPaletteId = this.name + "-reference-palette-wrapper";
        let ops = getViewSize(overlayPaletteId);
        setFittedSize(ops, ops, this.overlayPaletteSize, 5);

        setTransformSizes(this.transform, this.sliceFit, this.sliceImgSize);
        setFitZoom(this.transform, sFit.zoom, true);

        setTransformSizes(this.refImgTransform, this.refFit, this.refFit);
      }
    },
    refImgLocation(x, y) {
      const offset = viewOffset(this.refSize, this.refFit);
      if (!withinViewBounds(x, y, this.refSize, offset))
        return { x: -1, y: -1 };
      let loc = calculateRealLocation(x, y, offset, this.refImgTransform);
      return { x: loc.x / this.refFit.w, y: loc.y / this.refFit.h };
    },
    sliceLocation(x, y) {
      const imgOffset = viewOffset(this.sliceSize, this.sliceFit);
      if (!withinViewBounds(x, y, this.sliceSize, imgOffset))
        return { x: -1, y: -1 };
      return calculateRealLocation(x, y, imgOffset, this.transform);
    },
    sliceInBounds(x, y) {
      return (
        x >= 0 && y >= 0 && x <= this.sliceImgSize.w && y <= this.sliceImgSize.h
      );
    },
    selectExam(exam) {
      this.$store.dispatch("selectOCTExamCompareWithExamText", { examText: exam, name: this.name });
    },
    toggleColor() {
      this.$store.dispatch("toggleColorCompare", this.name);
    },
    invertColor() {
      this.$store.dispatch("toggleInvertedCompare", this.name);
    },
    toggleSegmentation() {
      this.$store.commit("toggleSegmentationCompare", this.name);
    },
    measure() {
      this.$store.commit("toggleMeasuringCompare", this.name);
      this.$store.commit("isBothMeasuring");
    },
    previousSlice() {
      this.$store.dispatch("selectPreviousSliceCompare", this.name);
    },
    nextSlice() {
      this.$store.dispatch("selectNextSliceCompare", this.name);
    },
    play() {
      this.$store.dispatch("playSlicesCompare", this.name);
    },
    pause() {
      this.$store.commit("pauseCompare", this.name);
    },
    setPlayRate(rate) {
      this.$store.commit("setPlayRateCompare", { view: this.name, rate });
    },
    selectSlice(index) {
      this.$store.dispatch("selectOCTImageByIndexProportionCompare", {
        keyword: "slice",
        index,
        view: this.name
      });
    },
    measurementChanged(x) {
      this.$store.commit("setMeasurementIndexCompare", { x, view: this.name });
    },
    toggleShowThicknessMap(){
      this.$store.commit("toggleShowThicknessMap");
    },
    updateOpacity(opacity) {
      this.$store.commit("updateThicknessMapOpacity", opacity);
    },
    calculateThicknessDifference() {
      this.$store.commit("calculateThicknessDifference");
    },
    wheel(e) {
      if (e.deltaY > 0) this.previousSlice();
      else this.nextSlice();
    }
  },
  computed: { 
    ...mapGetters([
      "showThicknessMap", 
      "thicknessMapOpacity", 
      "thicknessDifference",
      "isLocked", 
      "isBothMeasuring", 
      "baseline",
      "compare", 
      "groups", 
      "eye",
      "currentOCTBrowserType", 
      "preferredBrowserType"]),
    isCompare() {
      return this.name == "compare";
    },
    showSliceLines() {
      return !this.showThicknessMap || !this.isSpectralisVolume || !this.isCompare;
    },
    isSameSeriesType() 
    {
     let compareSeriesType = this.groups(this.baseline.exam, this.eye, this.currentOCTBrowserType, this.preferredBrowserType)[0].groupSeriesType.toLowerCase();
     let baselineSeriesType = this.groups(this.compare.exam, this.eye, this.currentOCTBrowserType, this.preferredBrowserType)[0].groupSeriesType.toLowerCase();
     return ((compareSeriesType.includes("volume") && baselineSeriesType.includes("volume")) || 
        (compareSeriesType.includes("cube") && baselineSeriesType.includes("cube"))) ? true : false;
    },
    showThicknessComparison() {
      return this.isCompare && this.isSpectralisVolume && this.baseline && (this.isCompare && this.baseline.group.scanType == this.context.group.scanType) && thicknessMap
        && this.isSameSeriesType;
    },
    hasExam() {
      return !!this.context;
    },
    exam() {
      return this.context && this.context.exam;
    },
    exSrNo() {
      return this.context && this.context.exSrNo;
    },
    groupName() {
      return this.context && this.context.groupName;
    },
    colorSettings() {
      return this.context && this.context.octColorSettings;
    },
    hasColorPalette() {
      return this.colorSettings && !!this.colorSettings.colorPalette;
    },
    isInverted() {
      return this.colorSettings && this.colorSettings.isInverted;
    },
    showSegmentation() {
      return this.context && this.context.showSegmentation;
    },
    isMeasuring() {
      return this.context && this.context.isMeasuring;
    },
    sliceInfo() {
      return this.context && this.context.sliceInfo;
    },
    hasSegmentation() {
      return this.sliceInfo && this.sliceInfo.sliceHasSegmentation;
    },
    octDataLoading() {
      return this.context && this.context.octDataLoading;
    },
    referenceImage() {
      return this.context && this.context.referenceImage;
    },
    refImgLoading() {
      return (
        this.referenceImage &&
        (this.referenceImage.imgIsLoading || this.referenceImage.imgIsQueued)
      );
    },
    refImgNotFound() {
      return !!(this.referenceImage && this.referenceImage.imgNotFound);
    },
    refImg() {
      return this.referenceImage && this.referenceImage.imgHtmlObject;
    },
    slices() {
      if (!this.context) return [];
      return this.$store.getters.getSlices(
        "slice",
        this.context.exSrNo,
        this.context.groupName
      );
    },
    sliceCurrent() {
      return this.context && this.context.sliceCurrent;
    },
    sliceCurrentIndex() {
      return this.sliceCurrent && this.sliceCurrent.index;
    },
    sliceBounds() {
      return this.context && this.context.sliceBounds;
    },
    isPlaying() {
      return !!(this.context && this.context.playIntervalId);
    },
    playRate() {
      return this.context && this.context.playRate;
    },
    sliceImg() {
      return this.sliceCurrent && this.sliceCurrent.imgHtmlObject;
    },
    sliceImgNotFound() {
      return !!(this.sliceCurrent && this.sliceCurrent.imgNotFound);
    },
    sliceImgLoading() {
      return (
        this.sliceCurrent &&
        (this.sliceCurrent.imgIsLoading || this.sliceCurrent.imgIsQueued)
      );
    },
    sliceCurrentEyeMarker() {
      return this.sliceCurrent && this.sliceCurrent.eyeMarker;
    },
    sliceSegmentationLayerData() {
      return this.sliceCurrent && this.sliceCurrent.sliceSegmentationLayerData;
    },
    measureData() {
      return this.context && this.context.measureData;
    },
    isSpectralisCircle(){
      return this.context && this.context.referenceImage && (this.context.referenceImage.groupName.toLowerCase().includes("circle") && !this.context.referenceImage.groupName.toLowerCase().includes("radialcircles"));
    },
    isSpectralisVolume(){
      return this.context && this.context.referenceImage && (this.context.referenceImage.groupName.toLowerCase().includes("volume") || this.context.referenceImage.groupName.toLowerCase().includes("cube"));
    },
    isSpectralisRadial(){
      return this.context && this.context.referenceImage && (this.context.referenceImage.groupName.toLowerCase().includes("star"));
    },
    isSpectralisRadialCircle(){
      return this.context && this.context.referenceImage && (this.context.referenceImage.groupName.toLowerCase().includes("radialcircles"));
    },
    thicknessMapPalette() {
      return this.context && this.context.thicknessMapPalette;
    },
    minThickness() {
      return this.context && this.context.thicknessMapMinThicknessUm;
    },
    maxThickness() {
      return this.context && this.context.thicknessMapMaxThicknessUm;
    },
    thicknessStep() {
      return this.context && this.context.thicknessMapThicknessStepUm;
    },
    thicknessMap() {
      return this.context && this.context.thicknessMap;
    },
    showDifferenceMap() {
      return this.isCompare && this.$store.getters.comparisonSameType;
    },
    differenceCanvas() {
      return this.context && this.context.differenceCanvas;
    },
    differenceMapOpacity() {
      return this.context && this.context.differenceMapOpacity;
    },
    differenceMin() {
      return this.context && this.context.differenceMapMinThicknessUm;
    },
    differenceMax() {
      return this.context && this.context.differenceMapMaxThicknessUm;
    },
    differenceStep() {
      return this.context && this.context.differenceMapThicknessStepUm;
    },
    differenceMapPalette() {
      return this.context && this.context.differenceMapPalette;
    },
    overlayImg() {
      return this.showDifferenceMap ? this.differenceCanvas : null;
    },
    overlayPalette() {
      return this.differenceMapPalette;
    },
    overlayMin() {
      return this.showDifferenceMap ? this.differenceMin : null;
    },
    overlayMax() {
      return this.showDifferenceMap ? this.differenceMax : null;
    },
    overlayStep() {
      return this.showDifferenceMap ? this.differenceStep : null;
    },
    thicknessMapImg() {
      return this.thicknessMap && this.thicknessMap.imgHtmlObject;
    },
    thicknessMapImgNotFound() {
      return !!(this.thicknessMap && this.thicknessMap.imgNotFound);
    },
    thicknessMapImgLoading() {
      return (
        this.thicknessMap &&
        (this.thicknessMap.imgIsLoading || this.thicknessMap.imgIsQueued)
      );
    },
    thicknessMapColorSettings() {
      return this.context && this.context.thicknessMapColorSettings;
    },
    foveaOffset() {
      return this.context && this.context.foveaOffset;
    },
    relativeMapSize() {
      return this.context && this.context.relativeMapSize;
    }
  },
  watch: {
    ...mapWatchers(["refImg", "sliceInfo", "exSrNo", "octDataLoading"], function() {
      this.resetView();
      this.updateCanvasSizes();
    }),
    ...mapWatchers(["measureData", "sliceSegmentationLayerData"], function() {
      this.calculateThicknessDifference();
    })
  },
  mounted() {
    this.resetView();
    this.updateCanvasSizes();
  }
};
</script>